import { useEffect, useMemo, useState } from "react";
import { hrEnums } from "../../Constant/HR_Staff/hrEnums";
import { RiAndroidFill, RiEyeFill } from "react-icons/ri";
import TableContainer from "../../BaseComponents/BaseTable";
import { Admin, All, ContactNo, Email, notFound, Search, SuperAdmin } from "../../Constant";
import { useNavigate } from "react-router-dom";
import {
  checkStatusCodeSuccess,
  errHandler,
  extractDate,
  getItem,
  getLocalItem,
  handleResponse,
  HRRole,
  No,
  Null,
  removeItem,
  removeStaffIdFromSessionStorage,
  setStaffIdInSessionStorage,
  Yes,
} from "../../Constant/common";
import { listOfStaffApi, staffAccessApi, viewStaffApi } from "../../Api/HRStaffApi";
import { viewSalaryStructureApi } from "../../Api/salaryApi";
import { getLetterConfigApi } from "../../Api/letterSettingsApi";
import BaseButton from "../../BaseComponents/BaseButton";
import { useSelectedCenter } from "../CenterContext";
import { useSelectedOrganization } from "../OrganizationContext";
import { staffTitle } from "../../Constant/title";
import BaseModal from "../../BaseComponents/BaseModal";
import { toast } from "react-toastify";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { Users, UserPlus, AlertCircle, FileText } from "react-feather";
import BaseTooltip from "../../BaseComponents/BaseTooltip";
import BaseDatePicker from "../../BaseComponents/BaseDatePicker";
import BaseInput from "../../BaseComponents/BaseInput";
import BaseCheckbox from "../../BaseComponents/BaseCheckbox";
import BaseSelect from "../../BaseComponents/BaseSelect";
import ActionDropdown from "../../BaseComponents/BaseActionDropdown";
import { getDefaultLetterBody, downloadLetterPdf } from "../../utils/letterUtils";

const StaffHR = () => {
  const [staffData, setStaffData] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [customPageSize, setCustomPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(false);
  const { setLoading } = usePageLoader();
  const [searchStaffData, setSearchStaffData] = useState([]);
  const [accessModal, setAccessModal] = useState(false);
  const [modalLoader, setModalLoader] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [letterModal, setLetterModal] = useState(false);
  const [letterModalLoader, setLetterModalLoader] = useState(false);
  const [letterDesignation, setLetterDesignation] = useState("");
  const [letterRelievingDate, setLetterRelievingDate] = useState(new Date());
  const [letterDate, setLetterDate] = useState(new Date());
  const [selectedStaffForLetter, setSelectedStaffForLetter] = useState(null);
  const [letterType, setLetterType] = useState("joining");
  const [letterBody, setLetterBody] = useState("");
  const [includeSignature, setIncludeSignature] = useState(true);
  const [signatory, setSignatory] = useState("HR Manager");
  const navigate = useNavigate();
  const { selectedCenterPayload } = useSelectedCenter();
  const { selectedOrganizationPayload } = useSelectedOrganization();

  useEffect(() => {
    document.title = staffTitle;
  }, []);

  const isSuperAdmin = localStorage.getItem("role") === SuperAdmin;

  useEffect(() => {
    removeItem("staffData");
  }, []);

  const selectedOrganizationId = selectedOrganizationPayload?.condition?.organizationId ?? null;

  useEffect(() => {
    if (!isSuperAdmin) return;
    setCurrentPage(1);
  }, [isSuperAdmin, selectedOrganizationId]);

  const fetchData = () => {
    if (isSuperAdmin && !selectedOrganizationId) return;
    setLoader(true);
    setLoading(true);
    const payload = {
      ...(isSuperAdmin
        ? { ...selectedOrganizationPayload, ...selectedCenterPayload }
        : selectedCenterPayload),
      condition: {
        organizationId: isSuperAdmin
          ? selectedOrganizationPayload?.condition?.organizationId
          : selectedCenterPayload?.condition?.organizationId,
      },
      order: [columnName, sortOrder],
      pageNumber: currentPage,
      pageSize: customPageSize,
    };
    listOfStaffApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setStaffData(resp?.data?.listOfStaffDetail);
          setTotalPages(resp?.data?.totalPages);
          setTotalRecords(resp?.data?.totalRecordsCount);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
        }
      })
      .catch((error) => {
        return error;
      })
      .finally(() => {
        setLoader(false);
        setLoading(false);
      });
  };

  const fetchSearchData = async () => {
    if (isSuperAdmin && !selectedOrganizationId) return;
    setLoader(true);
    setLoading(true);
    const payload = {
      ...(isSuperAdmin
        ? { ...selectedOrganizationPayload, ...selectedCenterPayload }
        : selectedCenterPayload),
      condition: {
        organizationId: isSuperAdmin
          ? selectedOrganizationPayload?.condition?.organizationId
          : selectedCenterPayload?.condition?.organizationId,
      },
      order: [columnName, sortOrder],
      search: searchValue,
      pageNumber: currentPage,
      pageSize: customPageSize,
    };

    await listOfStaffApi(payload)
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setSearchStaffData(resp?.data?.listOfStaffDetail);
          setTotalRecords(resp.data.totalRecordsCount);
          setTotalPages(resp?.data?.totalPages);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
        } else {
          setSearchStaffData([]);
        }
      })
      .catch((err) => {
        setSearchStaffData([]);
        return err;
      })
      .finally(() => {
        setLoader(false);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (searchValue === "") {
      setCurrentPage(1);
    }
  }, [searchValue]);

  useEffect(
    () => {
      if (searchValue) {
        fetchSearchData();
      } else {
        fetchData();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentPage,
      customPageSize,
      columnName,
      sortOrder,
      searchValue,
      selectedOrganizationId,
      selectedCenterPayload?.center_id,
    ],
  );

  const handleSearchValueChange = (value) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const handleFetchData = (page) => {
    setCurrentPage(page);
  };

  const handleFetchSorting = (page, id, order) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const handleGenerateJoiningLetter = async (staff) => {
    setSelectedStaffForLetter(staff);
    setLetterType("joining");
    const designation = staff?.position?.position_name || "";
    setLetterDesignation(designation);
    const now = new Date();
    setLetterDate(now);
    setLetterRelievingDate(now);
    setIncludeSignature(true);
    setSignatory("HR Manager");

    setLetterBody(getDefaultLetterBody("joining", staff, now, now, designation));
    setLetterModal(true);

    try {
      const [configRes, staffRes, salaryRes] = await Promise.allSettled([
        getLetterConfigApi(),
        staff?.id ? viewStaffApi(staff.id) : Promise.resolve(null),
        staff?.id ? viewSalaryStructureApi(staff.id) : Promise.resolve(null),
      ]);

      const config =
        configRes.status === "fulfilled" && checkStatusCodeSuccess(configRes.value?.statusCode)
          ? configRes.value.data
          : null;
      const fullStaff =
        staffRes.status === "fulfilled" && checkStatusCodeSuccess(staffRes.value?.statusCode)
          ? staffRes.value.data
          : staff;
      const salary =
        salaryRes.status === "fulfilled" && checkStatusCodeSuccess(salaryRes.value?.statusCode)
          ? salaryRes.value.data
          : null;

      if (fullStaff) {
        setSelectedStaffForLetter(fullStaff);
      }

      if (config?.settings) {
        setIncludeSignature(config.settings.include_signatory_signature ?? true);
        if (config.settings.signatory_name) {
          setSignatory(config.settings.signatory_name);
        }
      }

      setLetterBody(
        getDefaultLetterBody(
          "joining",
          fullStaff || staff,
          now,
          now,
          designation,
          config?.templates,
          salary,
        ),
      );
    } catch (e) {
      // Ignore background fetch error
    }
  };

  const handleGenerateExperienceLetterClick = async (staff) => {
    setSelectedStaffForLetter(staff);
    setLetterType("experience");
    const designation = staff?.position?.position_name || "";
    setLetterDesignation(designation);
    const now = new Date();
    setLetterDate(now);
    setLetterRelievingDate(now);
    setIncludeSignature(true);
    setSignatory("HR Manager");

    setLetterBody(getDefaultLetterBody("experience", staff, now, now, designation));
    setLetterModal(true);

    try {
      const [configRes, staffRes, salaryRes] = await Promise.allSettled([
        getLetterConfigApi(),
        staff?.id ? viewStaffApi(staff.id) : Promise.resolve(null),
        staff?.id ? viewSalaryStructureApi(staff.id) : Promise.resolve(null),
      ]);

      const config =
        configRes.status === "fulfilled" && checkStatusCodeSuccess(configRes.value?.statusCode)
          ? configRes.value.data
          : null;
      const fullStaff =
        staffRes.status === "fulfilled" && checkStatusCodeSuccess(staffRes.value?.statusCode)
          ? staffRes.value.data
          : staff;
      const salary =
        salaryRes.status === "fulfilled" && checkStatusCodeSuccess(salaryRes.value?.statusCode)
          ? salaryRes.value.data
          : null;

      if (fullStaff) {
        setSelectedStaffForLetter(fullStaff);
      }

      if (config?.settings) {
        setIncludeSignature(config.settings.include_signatory_signature ?? true);
        if (config.settings.signatory_name) {
          setSignatory(config.settings.signatory_name);
        }
      }

      setLetterBody(
        getDefaultLetterBody(
          "experience",
          fullStaff || staff,
          now,
          now,
          designation,
          config?.templates,
          salary,
        ),
      );
    } catch (e) {
      // Ignore background fetch error
    }
  };

  const handleDownloadLetter = async () => {
    if (!selectedStaffForLetter) return;
    setLetterModalLoader(true);

    try {
      let settings = {
        includeLogo: true,
        includeSignature: includeSignature,
        signatoryName: signatory || "Authorized Signatory",
        signatoryTitle: "Authorized Signatory",
      };

      try {
        const configRes = await getLetterConfigApi();
        if (checkStatusCodeSuccess(configRes?.statusCode) && configRes?.data?.settings) {
          const s = configRes.data.settings;
          settings = {
            includeLogo: s.include_logo ?? true,
            logoPath: s.logo_path || "",
            logoWidth: s.logo_width ? String(s.logo_width) : "120",
            logoAlign: s.logo_align || "left",
            includeSignature: includeSignature,
            signatoryName: signatory || s.signatory_name || "Authorized Signatory",
            signatoryTitle: s.signatory_title || "Authorized Signatory",
            signaturePath: s.signature_path || "",
          };
        }
      } catch (e) {
        // Fall back to default settings
      }

      const docName = letterType === "joining" ? "Joining_Letter" : "Experience_Letter";
      await downloadLetterPdf(docName, selectedStaffForLetter, letterBody, settings);

      toast.success(
        `${letterType === "joining" ? "Joining" : "Experience"} Letter PDF downloaded successfully.`,
      );
      setLetterModal(false);
    } catch (err) {
      errHandler(err);
      toast.error(
        `Failed to download ${letterType === "joining" ? "Joining" : "Experience"} Letter.`,
      );
    } finally {
      setLetterModalLoader(false);
    }
  };

  const handleView = (id) => {
    setStaffIdInSessionStorage(id);
    navigate(`/staffProfile/${id}`, {
      state: {
        viewProfileMode: true,
        viewGeneralMode: true,
        viewPersonalMode: true,
        viewEmploymentMode: true,
        viewBankMode: true,
      },
    });
  };

  const handleAdd = () => {
    removeStaffIdFromSessionStorage();
    if (isSuperAdmin) {
      if (selectedOrganizationId) {
        navigate("/staffProfile", {
          state: {
            viewProfileMode: false,
            viewGeneralMode: false,
            viewPersonalMode: false,
            viewEmploymentMode: false,
            viewBankMode: false,
            organizationId: selectedOrganizationId,
          },
        });
      } else {
        navigate("/staffOrganizationConfig");
      }
    } else {
      navigate("/staffProfile", {
        state: {
          viewProfileMode: false,
          viewGeneralMode: false,
          viewPersonalMode: false,
          viewEmploymentMode: false,
          viewBankMode: false,
        },
      });
    }
  };

  const classColumns = useMemo(
    () => [
      {
        header: hrEnums.SR_NO,
        accessorKey: "serial_number",
        cell: (cell) => (currentPage - 1) * customPageSize + cell.row.index + 1,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: hrEnums.STAFF_NAME,
        accessorKey: "name",
        enableColumnFilter: false,
      },
      {
        header: hrEnums.PRIMARY_CENTER,
        accessorKey: "primaryCenter.center_name",
        enableColumnFilter: false,
      },
      {
        header: ContactNo,
        accessorKey: "contact_no",
        enableColumnFilter: false,
      },
      {
        header: Email,
        accessorKey: "email",
        enableColumnFilter: false,
      },
      {
        header: hrEnums.JOINING_DATE,
        accessorKey: "staffGeneralInformation.date_of_joining",
        enableColumnFilter: false,
        cell: (cell) => {
          return (
            extractDate(cell.row.original.staffGeneralInformation?.date_of_joining) ??
            handleResponse.nullData
          );
        },
      },
      {
        header: hrEnums.ACTION,
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (cell) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <BaseTooltip label={hrEnums.VIEW} color="#047857">
              <button
                onClick={() => handleView(cell.row.original.id)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(16,185,129,0.08)",
                  border: "1px solid rgba(16,185,129,0.2)",
                  color: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <RiEyeFill size={14} />
              </button>
            </BaseTooltip>
            {getItem("selected_center_id") !== Null &&
              (getLocalItem("role") === HRRole || getLocalItem("role") === Admin) && (
                <BaseTooltip
                  label={cell.row.original?.isAppAccess ? "Revoke Access" : "Grant Access"}
                  color={cell.row.original?.isAppAccess ? "#be123c" : "#4f46e5"}
                >
                  <button
                    onClick={() => {
                      setAccessModal(true);
                      setSelectedStaff(cell.row.original);
                    }}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 8,
                      background: cell.row.original?.isAppAccess
                        ? "rgba(244,63,94,0.08)"
                        : "rgba(99,102,241,0.08)",
                      border: `1px solid ${cell.row.original?.isAppAccess ? "rgba(244,63,94,0.2)" : "rgba(99,102,241,0.2)"}`,
                      color: cell.row.original?.isAppAccess ? "#f43f5e" : "#6366f1",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <RiAndroidFill size={14} />
                  </button>
                </BaseTooltip>
              )}
            <ActionDropdown
              isOpen={openDropdownId === cell.row.original.id}
              onToggle={() =>
                setOpenDropdownId(
                  openDropdownId === cell.row.original.id ? null : cell.row.original.id,
                )
              }
              onClose={() => setOpenDropdownId(null)}
              actions={[
                {
                  label: "Joining Letter",
                  icon: FileText,
                  onClick: () => {
                    setOpenDropdownId(null);
                    handleGenerateJoiningLetter(cell.row.original);
                  },
                },
                {
                  label: "Experience Letter",
                  icon: FileText,
                  onClick: () => {
                    setOpenDropdownId(null);
                    handleGenerateExperienceLetterClick(cell.row.original);
                  },
                },
              ]}
            />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openDropdownId, currentPage, customPageSize],
  );

  const handleAccess = () => {
    setModalLoader(false);
    const payload = {
      id: selectedStaff?.id,
      appAccess: !selectedStaff?.isAppAccess,
    };
    staffAccessApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          toast.success(res?.message);
          fetchData();
          setAccessModal(false);
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        errHandler(err);
      })
      .finally(() => {
        setModalLoader(false);
      });
  };

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Users size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {hrEnums.STAFF}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0 ? `${totalRecords} staff members` : "Manage your staff"}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={handleAdd}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <UserPlus size={15} strokeWidth={2} /> {hrEnums.ADD}
        </BaseButton>
      </div>

      {/* ── Access modal ── */}
      <BaseModal
        isOpen={accessModal}
        title="App Access"
        toggler={() => setAccessModal(false)}
        submit={() => handleAccess()}
        submitText={Yes}
        closeText={No}
        disabled={modalLoader}
        loader={modalLoader}
      >
        <p style={{ margin: 0, color: "var(--text-primary)" }}>
          {!selectedStaff?.isAppAccess
            ? hrEnums.ACCESS_CONFIRMATION
            : hrEnums.REMOVE_ACCESS_CONFIRMATION}
        </p>
      </BaseModal>

      {/* ── Table ── */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {staffData && staffData?.length > 0 ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            fetchData={handleFetchData}
            customPageSize={
              customPageSize === All && totalRecords > 0 ? totalRecords : customPageSize
            }
            setCustomPageSize={setCustomPageSize}
            fetchSortingData={handleFetchSorting}
            columns={classColumns}
            shouldLineBreak={false}
            data={searchValue ? searchStaffData : staffData || []}
            manualPagination={true}
            SearchPlaceholder={Search}
            onSearch={handleSearchValueChange}
            isGlobalFilter={true}
            manualFiltering={true}
          />
        ) : !loader ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>{notFound.dataNotFound}</p>
          </div>
        ) : null}
      </div>

      {/* ── Experience/Joining Letter Generation Modal ── */}
      <BaseModal
        isOpen={letterModal}
        title={letterType === "joining" ? "Generate Joining Letter" : "Generate Experience Letter"}
        toggler={() => setLetterModal(false)}
        submit={handleDownloadLetter}
        submitText="Generate & Download"
        disabled={letterModalLoader}
        loader={letterModalLoader}
      >
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}
        >
          {/* Scrollable content area - contains all form fields */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              paddingRight: 4,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {/* Metadata configuration */}
            <div
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, flexShrink: 0 }}
            >
              <div>
                <label className="form-label d-block mb-1 text-secondary fw-semibold small">
                  Designation
                </label>
                <BaseInput
                  name="letterDesignation"
                  type="text"
                  placeholder="Designation"
                  value={letterDesignation}
                  handleChange={(e) => {
                    setLetterDesignation(e.target.value);
                    setLetterBody(
                      getDefaultLetterBody(
                        letterType,
                        selectedStaffForLetter,
                        letterRelievingDate,
                        letterDate,
                        e.target.value,
                      ),
                    );
                  }}
                />
              </div>
              <div>
                <label className="form-label d-block mb-1 text-secondary fw-semibold small">
                  Letter Date
                </label>
                <BaseDatePicker
                  name="letterDate"
                  value={letterDate}
                  onChange={(d) => {
                    setLetterDate(d);
                    setLetterBody(
                      getDefaultLetterBody(
                        letterType,
                        selectedStaffForLetter,
                        letterRelievingDate,
                        d,
                        letterDesignation,
                      ),
                    );
                  }}
                  placeholder="Select date"
                  popperPlacement="right-start"
                />
              </div>
            </div>

            {letterType === "experience" && (
              <div>
                <label className="form-label d-block mb-1 text-secondary fw-semibold small">
                  Relieving Date (Last Working Day)
                </label>
                <BaseDatePicker
                  name="letterRelievingDate"
                  value={letterRelievingDate}
                  onChange={(d) => {
                    setLetterRelievingDate(d);
                    setLetterBody(
                      getDefaultLetterBody(
                        letterType,
                        selectedStaffForLetter,
                        d,
                        letterDate,
                        letterDesignation,
                      ),
                    );
                  }}
                  placeholder="Select date"
                  popperPlacement="right-start"
                />
              </div>
            )}

            {/* Letter Body Editor */}
            <div>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="form-label m-0 text-secondary fw-semibold small">
                  Letter Body Text
                </label>
                <BaseButton
                  type="button"
                  onClick={() =>
                    setLetterBody(
                      getDefaultLetterBody(
                        letterType,
                        selectedStaffForLetter,
                        letterRelievingDate,
                        letterDate,
                        letterDesignation,
                      ),
                    )
                  }
                  color="link"
                  className="p-0 text-decoration-none shadow-none"
                  style={{
                    color: "var(--brand-indigo)",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                >
                  Reset to Default
                </BaseButton>
              </div>
              <BaseInput
                type="textarea"
                name="letterBody"
                value={letterBody}
                handleChange={(e) => setLetterBody(e.target.value)}
                placeholder="Enter letter body text"
                rows={12}
                className="mb-0"
              />
            </div>

            <div style={{ borderTop: "1px solid var(--glass-border)", paddingTop: 10 }}>
              <BaseCheckbox
                id="includeSignatureIndex"
                label="Include Digital Signature & Company Stamp"
                checked={includeSignature}
                onChange={(e) => setIncludeSignature(e.target.checked)}
                className="d-flex align-items-center gap-2 mb-0"
                labelClassName="mb-0 text-primary fw-medium fs-7"
              />
            </div>

            {/* Signatory selector */}
            {includeSignature && (
              <div>
                <label className="form-label d-block mb-1 text-secondary fw-semibold small">
                  Select Signatory Authority
                </label>
                <BaseSelect
                  name="signatory"
                  value={signatory}
                  handleChange={(name, val) => setSignatory(val)}
                  options={[
                    { value: "HR Manager", label: "HR Manager" },
                    { value: "Managing Director", label: "Managing Director" },
                    { value: "CEO", label: "CEO" },
                  ]}
                  mb="mb-0"
                />
              </div>
            )}
          </div>
        </div>
      </BaseModal>
    </div>
  );
};

export default StaffHR;
